import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import './Profiles.css';

const Concesionarios = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [concesionarios, setConcesionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [showModal, setShowModal] = useState(false);
  const [editingConcesionario, setEditingConcesionario] = useState(null);
  const [transitos, setTransitos] = useState([]);
  const [associatedTransits, setAssociatedTransits] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    nit: '',
    address: '',
    phone: '',
    status: 'activo'
  });

  // Estado para gestión de Zonas
  const [showZonasModal, setShowZonasModal] = useState(false);
  const [selectedConcForZonas, setSelectedConcForZonas] = useState(null);
  const [zonas, setZonas] = useState([]);
  const [zonaLoading, setZonaLoading] = useState(false);
  const [editingZona, setEditingZona] = useState(null);
  const [zonaFormData, setZonaFormData] = useState({ name: '', description: '' });
  const [zonaError, setZonaError] = useState('');

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/dashboard');
    }
    fetchConcesionarios();
    fetchTransitos();
  }, [user, navigate]);

  const fetchTransitos = async () => {
    try {
      const res = await axios.get('/api/transitos');
      setTransitos(res.data);
    } catch (err) {
      console.error('Error al cargar tránsitos:', err);
    }
  };

  const fetchConcesionarios = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/concesionarios');
      setConcesionarios(res.data);
      setError('');
    } catch (err) {
      setError('Error al cargar concesionarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- LÓGICA DE ZONAS ---
  const handleOpenZonasModal = async (concesionario) => {
    setSelectedConcForZonas(concesionario);
    setEditingZona(null);
    setZonaFormData({ name: '', description: '' });
    setZonaError('');
    setShowZonasModal(true);
    await fetchZonas(concesionario.id);
  };

  const fetchZonas = async (concesionarioId) => {
    try {
      setZonaLoading(true);
      const res = await axios.get(`/api/zonas?concesionario_id=${concesionarioId}`);
      setZonas(res.data);
    } catch (err) {
      console.error('Error al cargar zonas:', err);
      setZonaError('Error al cargar zonas');
    } finally {
      setZonaLoading(false);
    }
  };

  const handleSaveZona = async (e) => {
    e.preventDefault();
    if (!zonaFormData.name.trim()) return;

    try {
      if (editingZona) {
        await axios.put(`/api/zonas/${editingZona.id}`, zonaFormData);
      } else {
        await axios.post('/api/zonas', {
          ...zonaFormData,
          concesionario_id: selectedConcForZonas.id
        });
      }
      setZonaFormData({ name: '', description: '' });
      setEditingZona(null);
      setZonaError('');
      fetchZonas(selectedConcForZonas.id);
    } catch (err) {
      console.error('Error al guardar zona:', err);
      setZonaError(err.response?.data?.message || 'Error al guardar la zona');
    }
  };

  const handleDeleteZona = async (zonaId) => {
    if (!window.confirm('¿Está seguro de eliminar esta zona? Los puntos de venta mantendrán su concesionario pero quedarán sin zona.')) return;
    try {
      await axios.delete(`/api/zonas/${zonaId}`);
      fetchZonas(selectedConcForZonas.id);
    } catch (err) {
      console.error('Error al eliminar zona:', err);
      setZonaError(err.response?.data?.message || 'Error al eliminar la zona');
    }
  };

  const handleOpenModal = async (concesionario = null) => {
    if (concesionario) {
      setEditingConcesionario(concesionario);
      setFormData({
        name: concesionario.name,
        nit: concesionario.nit || '',
        address: concesionario.address || '',
        phone: concesionario.phone || '',
        status: concesionario.status
      });
      try {
        const res = await axios.get(`/api/concesionarios/${concesionario.id}/transitos`);
        setAssociatedTransits(res.data);
      } catch (err) {
        console.error('Error al cargar tránsitos asociados:', err);
        setAssociatedTransits([]);
      }
    } else {
      setEditingConcesionario(null);
      setFormData({
        name: '',
        nit: '',
        address: '',
        phone: '',
        status: 'activo'
      });
      setAssociatedTransits([]);
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      let concessionaireId = editingConcesionario?.id;
      if (editingConcesionario) {
        await axios.put(`/api/concesionarios/${editingConcesionario.id}`, formData);
      } else {
        const res = await axios.post('/api/concesionarios', formData);
        concessionaireId = res.data.id;
      }
      
      // Guardar asociaciones
      await axios.post(`/api/concesionarios/${concessionaireId}/transitos`, {
        transito_ids: associatedTransits
      });
      
      fetchConcesionarios();
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar concesionario');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = concesionarios.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="🏪 Gestión de Concesionarios (Tramitadores)" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          <div className="page-header">
            <div className="header-title">
              <h2>Concesionarios Registrados</h2>
              <p className="subtitle">Empresas encargadas de iniciar trámites</p>
            </div>
            <button onClick={() => handleOpenModal()} className="btn-primary">
              ➕ Nuevo Concesionario
            </button>
          </div>

          {loading ? (
            <p className="loading">Cargando concesionarios...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>NIT</th>
                    <th>Dirección</th>
                    <th>Teléfono</th>
                    <th>Tránsitos Autorizados</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(c => (
                    <tr key={c.id}>
                      <td className="user-name">{c.name}</td>
                      <td>{c.nit}</td>
                      <td>{c.address}</td>
                      <td>{c.phone}</td>
                      <td>
                        <span className="profile-badge" style={{ display: 'inline-block', minWidth: '24px', textAlign: 'center' }}>
                          🏢 {c.transitos_count || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`status ${c.status}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="actions" style={{ display: 'flex', gap: '6px' }}>
                        <button 
                          onClick={() => handleOpenZonasModal(c)} 
                          className="btn-action btn-secondary"
                          title="Gestionar Zonas de este concesionario"
                          style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                        >
                          🗺️ Zonas
                        </button>
                        <button 
                          onClick={() => handleOpenModal(c)} 
                          className="btn-action btn-edit"
                          title="Editar Concesionario"
                        >
                          ✏️ Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              
              <Pagination 
                currentPage={currentPage}
                totalItems={concesionarios.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editingConcesionario ? 'Editar Concesionario' : 'Nuevo Concesionario'}</h3>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label>Nombre del Concesionario</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>NIT</label>
                <input
                  type="text"
                  name="nit"
                  value={formData.nit}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Dirección</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              {editingConcesionario && (
                <div className="form-group">
                  <label>Estado</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              )}

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                  🏢 Tránsitos Autorizados para Envío de Trámites
                </label>
                <div style={{ 
                  maxHeight: '150px', 
                  overflowY: 'auto', 
                  border: '1px solid var(--card-border)', 
                  borderRadius: '6px', 
                  padding: '10px',
                  background: 'var(--card-bg)'
                }}>
                  {transitos.map(t => (
                    <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '13px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={associatedTransits.includes(t.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssociatedTransits(prev => [...prev, t.id]);
                          } else {
                            setAssociatedTransits(prev => prev.filter(id => id !== t.id));
                          }
                        }}
                      />
                      🏢 {t.name}
                    </label>
                  ))}
                  {transitos.length === 0 && (
                    <span style={{ fontSize: '12px', color: '#64748b' }}>No hay tránsitos registrados.</span>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingConcesionario ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE GESTIÓN DE ZONAS DE CONCESIONARIO */}
      {showZonasModal && selectedConcForZonas && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }}>
            <h3>🗺️ Zonas de {selectedConcForZonas.name}</h3>
            <p className="subtitle" style={{ fontSize: '13px', color: '#64748b', marginBottom: '15px' }}>
              Defina las zonas geográficas/operacionales (opcional) para agrupar puntos de venta y reportes.
            </p>

            {zonaError && <div className="error-banner" style={{ marginBottom: '10px' }}>{zonaError}</div>}

            {/* Formulario de creación/edición de Zona */}
            <form onSubmit={handleSaveZona} style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: 'var(--card-bg)', padding: '12px', borderRadius: '8px', border: '1px solid var(--card-border)', marginBottom: '15px' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  placeholder="Nombre de la Zona (Ej: Norte, Centro)"
                  value={zonaFormData.name}
                  onChange={(e) => setZonaFormData({ ...zonaFormData, name: e.target.value })}
                  style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  required
                />
                <button type="submit" className="btn-primary" style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}>
                  {editingZona ? '✏️ Actualizar' : '➕ Agregar Zona'}
                </button>
                {editingZona && (
                  <button 
                    type="button" 
                    onClick={() => { setEditingZona(null); setZonaFormData({ name: '', description: '' }); }}
                    className="btn-secondary"
                    style={{ padding: '8px 12px' }}
                  >
                    Cancelar
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder="Descripción adicional (Opcional)"
                value={zonaFormData.description}
                onChange={(e) => setZonaFormData({ ...zonaFormData, description: e.target.value })}
                style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
              />
            </form>

            {/* Lista de Zonas existentes */}
            {zonaLoading ? (
              <p className="loading">Cargando zonas...</p>
            ) : (
              <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {zonas.length === 0 ? (
                  <p style={{ fontStyle: 'italic', color: '#64748b', textAlign: 'center', padding: '15px' }}>
                    Este concesionario no tiene zonas registradas. (Los puntos de venta funcionarán normalmente sin zona)
                  </p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                        <th style={{ padding: '8px' }}>Nombre</th>
                        <th style={{ padding: '8px' }}>Descripción</th>
                        <th style={{ padding: '8px' }}>Puntos de Venta</th>
                        <th style={{ padding: '8px', textAlign: 'right' }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zonas.map(z => (
                        <tr key={z.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px', fontWeight: 'bold' }}>📍 {z.name}</td>
                          <td style={{ padding: '8px', color: '#64748b' }}>{z.description || '-'}</td>
                          <td style={{ padding: '8px' }}>
                            <span className="profile-badge" style={{ fontSize: '12px' }}>
                              🏪 {z.puntos_venta_count || 0}
                            </span>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingZona(z);
                                setZonaFormData({ name: z.name, description: z.description || '' });
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '8px', fontSize: '14px' }}
                              title="Editar Zona"
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteZona(z.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}
                              title="Eliminar Zona"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button type="button" onClick={() => setShowZonasModal(false)} className="btn-secondary">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Concesionarios;
